/**
 * Small, dependency-free formatting helpers for the "My consumption" screen (`QR-05`'s "every figure
 * carries its age"). Deliberately not a date-formatting library: the portal's brand guidance
 * (`STYLES.md`) asks for plain, confident copy, and a relative-age sentence is easier to read at a
 * glance than an absolute timestamp.
 */

// American English throughout (STYLES.md), rather than the runtime's own default locale — a
// portal shown to UK, Romanian and German-based engineers must render "$45.00", not something that
// varies with the server or browser's configured locale.
const DisplayLocale = "en-US";

/**
 * Formats an amount of money for display.
 *
 * @param amount - The amount to format.
 * @param currency - The ISO 4217 currency code.
 * @returns The formatted amount, for example `"$42.00"`.
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(DisplayLocale, { style: "currency", currency }).format(amount);
  } catch {
    // An unrecognised currency code (shouldn't happen — every code here comes from the vendor or
    // the reporting currency constant) falls back to a plain number rather than throwing.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Formats an amount of money without trailing zero cents, for allowances set in whole units.
 *
 * @param amount - The amount to format.
 * @param currency - The ISO 4217 currency code.
 * @returns The formatted amount, for example `"$2,200"` or `"$291.71"`.
 */
export function formatWholeMoney(amount: number, currency: string): string {
  const cents = Math.round(amount * 100) % 100 !== 0;
  try {
    return new Intl.NumberFormat(DisplayLocale, { style: "currency", currency, minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${String(amount)} ${currency}`;
  }
}

/**
 * Formats an amount rounded to the whole unit, with no cents even when it has them: for a figure read at a glance, such as the
 * organisation's spend, where cents are noise.
 *
 * @param amount - The amount to format.
 * @param currency - The ISO 4217 currency code.
 * @returns The formatted amount, for example `"$49,294"`.
 */
export function formatRoundedMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(DisplayLocale, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${String(Math.round(amount))} ${currency}`;
  }
}

/**
 * Formats a count (tokens, requests) with thousands separators.
 *
 * @param count - The count to format.
 * @returns The formatted count, for example `"15,000"`.
 */
export function formatCount(count: number): string {
  return new Intl.NumberFormat(DisplayLocale).format(count);
}

/**
 * Formats a count compactly for a small figure, for example `"3.4M"` or `"10.6K"`. Counts under ten
 * thousand are written out in full, since abbreviating them saves nothing.
 *
 * @param count - The count to format.
 * @returns The compact count.
 */
export function formatCompactCount(count: number): string {
  if (Math.abs(count) < 10_000) {
    return formatCount(count);
  }
  return new Intl.NumberFormat(DisplayLocale, { notation: "compact", maximumFractionDigits: 1 }).format(count);
}

/**
 * Formats an ISO timestamp as a short relative-age sentence fragment — "2 minutes", "3 hours",
 * "5 days" — for interpolation into a translated "as of {{age}} ago" sentence.
 *
 * @param isoTimestamp - The instant to describe the age of.
 * @param now - The current instant to compare against (defaults to the real current time).
 * @returns A short age fragment, or the formatted date itself once it is more than 30 days old.
 */
export function formatAge(isoTimestamp: string, now: Date = new Date()): string {
  const then = new Date(isoTimestamp);
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60000));

  if (minutes < 1) {
    return "moments";
  }

  if (minutes < 60) {
    return `${String(minutes)} minute${minutes === 1 ? "" : "s"}`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${String(hours)} hour${hours === 1 ? "" : "s"}`;
  }

  const days = Math.round(hours / 24);
  if (days < 30) {
    return `${String(days)} day${days === 1 ? "" : "s"}`;
  }

  return then.toLocaleDateString(DisplayLocale);
}

/**
 * Formats an ISO timestamp as a short clock time, for "first figures expected at HH:MM".
 *
 * @param isoTimestamp - The instant to format.
 * @returns A short time, for example `"2:30 PM"`.
 */
export function formatClockTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleTimeString(DisplayLocale, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Formats an ISO timestamp as a date and time in the user's local timezone.
 *
 * @param isoTimestamp - The instant to format.
 * @returns A formatted date and time, for example `"1 Oct 2026, 2:30 PM"`.
 */
export function formatDateTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const formattedDate = date.toLocaleDateString(DisplayLocale, { day: "numeric", month: "short", year: "numeric" });
  const formattedTime = date.toLocaleTimeString(DisplayLocale, { hour: "2-digit", minute: "2-digit" });
  return `${formattedDate}, ${formattedTime}`;
}

/**
 * The date a monthly allowance next resets: the first day of the next UTC calendar month, the period the backend measures
 * spend over.
 *
 * @param now - The current instant (defaults to the real current time).
 * @returns The reset date as an ISO calendar date, for example `"2026-11-01"`.
 */
export function allowanceResetDate(now: Date = new Date()): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return next.toISOString().slice(0, 10);
}

/**
 * Formats an ISO calendar date (`YYYY-MM-DD`) for display.
 *
 * @param isoDate - The date to format.
 * @returns A short date, for example `"1 Oct 2026"`.
 */
export function formatDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(DisplayLocale, { day: "numeric", month: "short", year: "numeric" });
}
